import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, MapPin, ArrowRight, Video, Clock, WifiOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useOfflineCache } from "@/hooks/useOfflineCache";
import { Badge } from "@/components/ui/badge";

const tourDates = [
  {
    date: "2026",
    city: "Texas, yeah, Texas, and we'll have some fun.",
    venue: "TBA",
  },
];

interface StreamEvent {
  id: string;
  title: string;
  event_date: string;
  ticket_price: number;
  status: string;
}

function useCountdown(targetDate: Date) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0 });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;

      if (distance < 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0 });
        return;
      }

      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
}

const UpcomingStreamCard = ({ stream }: { stream: StreamEvent }) => {
  const countdown = useCountdown(new Date(stream.event_date));
  const isLive = stream.status === "live";

  return (
    <Card className={`border-border ${isLive ? "border-red-500/50 bg-red-500/5" : "bg-primary/5 border-primary/20"} hover:border-primary transition-all duration-300`}>
      <CardContent className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-6">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6 flex-1">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isLive ? "bg-red-500/20" : "bg-primary/20"}`}>
                <Video className={`h-5 w-5 ${isLive ? "text-red-500" : "text-primary"}`} />
              </div>
              {isLive ? (
                <span className="flex items-center gap-2 text-red-500 font-bold">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  LIVE NOW
                </span>
              ) : (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Clock className="h-4 w-4" />
                  <span>{countdown.days}d {countdown.hours}h {countdown.minutes}m</span>
                </div>
              )}
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-base md:text-lg tracking-[0.02em] text-foreground mb-0.5">{stream.title}</h3>
              <p className="text-xs md:text-sm font-medium text-muted-foreground">
                {new Date(stream.event_date).toLocaleDateString("en-US", { 
                  weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" 
                })}
              </p>
            </div>
          </div>
          <Link to="/stream">
            <Button className={`${isLive ? "bg-red-500 hover:bg-red-600" : "bg-primary hover:bg-primary/90"} text-primary-foreground font-bold tracking-[0.03em] uppercase text-sm min-w-[140px] h-11`}>
              {isLive ? "Watch Now" : `$${stream.ticket_price} - Buy`}
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

const TourSection = () => {
  const [upcomingStreams, setUpcomingStreams] = useState<StreamEvent[]>([]);
  const [isOfflineData, setIsOfflineData] = useState(false);
  const { isReady, cacheData, getCachedData } = useOfflineCache<StreamEvent>("gigs");

  useEffect(() => {
    const loadStreams = async () => {
      // Try to fetch from network first
      if (navigator.onLine) {
        const { data } = await supabase
          .from("livestream_events")
          .select("id, title, event_date, ticket_price, status")
          .in("status", ["upcoming", "live"])
          .order("event_date", { ascending: true })
          .limit(2);

        if (data) {
          setUpcomingStreams(data);
          setIsOfflineData(false);
          // Cache for offline use
          if (isReady) {
            cacheData(data);
          }
        }
      } else if (isReady) {
        // Offline - try to get cached data
        const cached = await getCachedData();
        if (cached && cached.length > 0) {
          setUpcomingStreams(cached);
          setIsOfflineData(true);
        }
      }
    };

    loadStreams();
  }, [isReady, cacheData, getCachedData]);

  return (
    <section id="tour" className="py-12 md:py-20 bg-card scroll-mt-16 md:scroll-mt-20">
      <div className="container mx-auto px-4">
        <h2 className="text-[28px] md:text-[36px] font-bold text-center mb-8 md:mb-12 tracking-[0.02em] uppercase text-foreground">
          TOUR
        </h2>

        {/* Upcoming Streams */}
        {upcomingStreams.length > 0 && (
          <div className="max-w-5xl mx-auto mb-8">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-sm font-bold text-primary uppercase tracking-wider flex items-center gap-2">
                <Video className="h-4 w-4" />
                Upcoming Livestreams
              </h3>
              {isOfflineData && (
                <Badge variant="outline" className="text-xs gap-1 bg-muted/50">
                  <WifiOff className="h-3 w-3" />
                  Cached
                </Badge>
              )}
            </div>
            <div className="space-y-3">
              {upcomingStreams.map((stream) => (
                <UpcomingStreamCard key={stream.id} stream={stream} />
              ))}
            </div>
          </div>
        )}



        {tourDates.length > 0 ? (
          <div className="max-w-5xl mx-auto space-y-3 md:space-y-4">
            {tourDates.map((show, index) => (
              <Card key={index} className="bg-background border-border hover:border-primary active:bg-muted/30 transition-all duration-300">
                <CardContent className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-6">
                    <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6 flex-1">
                      <div className="flex items-center gap-3 min-w-[120px]">
                        <Calendar className="h-5 w-5 text-primary flex-shrink-0" />
                        <div className="text-base md:text-lg font-bold uppercase tracking-[0.02em] text-foreground">{show.date}</div>
                      </div>
                      <div className="flex items-start gap-3 flex-1">
                        <MapPin className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <div>
                          <h3 className="font-bold text-base md:text-lg tracking-[0.02em] text-foreground mb-0.5 md:mb-1">{show.city}</h3>
                          <p className="text-xs md:text-sm font-medium text-muted-foreground uppercase tracking-[0.02em]">{show.venue}</p>
                        </div>
                      </div>
                    </div>
                    <Button className="bg-primary hover:bg-primary/90 active:bg-primary/80 text-primary-foreground font-bold tracking-[0.03em] uppercase text-sm md:text-[15px] min-w-[140px] h-11 md:h-10 touch-target" disabled>
                      COMING SOON
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            <div className="text-center pt-6">
              <Link to="/tour">
                <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground gap-2">
                  View All Tour Dates
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto text-center py-8 md:py-12 px-4">
            <p className="text-lg md:text-xl font-medium text-muted-foreground mb-4">No upcoming tour dates at the moment.</p>
            <p className="text-base md:text-lg font-medium text-muted-foreground/60 mb-6">Join our mailing list to be the first to know when new shows are announced.</p>
            <Link to="/tour">
              <Button variant="outline" className="border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground gap-2">
                Visit Tour Page
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default TourSection;
